import type { MediaItem, MediaCategory, SortKey } from "@/types";
import { mediaItems } from "@/mocks/mediaItems";

export interface MediaFilter {
  category?: "全部" | MediaCategory;
  region?: string;
  year?: string;
  keyword?: string;
}

function matchesKeyword(item: MediaItem, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  const haystack = [
    item.title,
    item.summary,
    item.region,
    ...item.tags,
    item.director ?? "",
    ...(item.cast ?? []),
    ...item.content,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(kw);
}

export function filterMedia(list: MediaItem[], filter: MediaFilter): MediaItem[] {
  return list.filter((item) => {
    if (filter.category && filter.category !== "全部" && item.category !== filter.category) {
      return false;
    }
    if (filter.region && filter.region !== "全部" && item.region !== filter.region) {
      return false;
    }
    if (filter.year && filter.year !== "全部" && String(item.year) !== filter.year) {
      return false;
    }
    if (filter.keyword && !matchesKeyword(item, filter.keyword)) {
      return false;
    }
    return true;
  });
}

export function sortMedia(list: MediaItem[], sort: SortKey): MediaItem[] {
  const copy = [...list];
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.sort(
    (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
  );
}

export function searchMedia(keyword: string, filter: Omit<MediaFilter, "keyword"> = {}): MediaItem[] {
  return sortMedia(filterMedia(mediaItems, { ...filter, keyword }), "time");
}

export function getMediaById(id: string): MediaItem | undefined {
  return mediaItems.find((item) => item.id === id);
}

export function getRelated(item: MediaItem, limit = 4): MediaItem[] {
  return mediaItems
    .filter((m) => m.id !== item.id)
    .sort((a, b) => {
      const scoreA = (a.category === item.category ? 2 : 0) + a.tags.filter((t) => item.tags.includes(t)).length;
      const scoreB = (b.category === item.category ? 2 : 0) + b.tags.filter((t) => item.tags.includes(t)).length;
      return scoreB - scoreA;
    })
    .slice(0, limit);
}

export function getFeatured(): MediaItem[] {
  const featured = mediaItems.filter((item) => item.isFeatured);
  return featured.length ? featured : mediaItems.slice(0, 4);
}

export function getLatest(limit = 6): MediaItem[] {
  return sortMedia(mediaItems, "time").slice(0, limit);
}

export function getHot(limit = 4): MediaItem[] {
  return sortMedia(mediaItems, "hot").slice(0, limit);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return String(views);
}

export function formatDate(date: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}